import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CognitoIdentityProviderClient, InitiateAuthCommand, RespondToAuthChallengeCommand } from '@aws-sdk/client-cognito-identity-provider';
import { AuthContextType, User } from '../types';

// These should come from environment variables in production
const AWS_REGION = process.env.REACT_APP_AWS_REGION || 'us-east-1';
const USER_POOL_ID = process.env.REACT_APP_USER_POOL_ID || '';
const CLIENT_ID = process.env.REACT_APP_CLIENT_ID || '';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const cognitoClient = new CognitoIdentityProviderClient({
    region: AWS_REGION,
  });

  useEffect(() => {
    // Check if user is already authenticated on app load
    const checkAuthStatus = async () => {
      try {
        const storedToken = localStorage.getItem('accessToken');
        const storedUser = localStorage.getItem('user');
        
        if (storedToken && storedUser) {
          // Validate token by making a test API call
          // For now, we'll just trust the stored token
          setAccessToken(storedToken);
          setUser(JSON.parse(storedUser));
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        // Clear invalid stored data
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const signIn = async (username: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);

      const command = new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: CLIENT_ID,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
        },
      });

      const response = await cognitoClient.send(command);

      if (response.AuthenticationResult?.IdToken) {
        const idToken = response.AuthenticationResult.IdToken;
        const accessToken = response.AuthenticationResult.AccessToken;
        
        // Parse JWT to get user info from ID token (ID token has user info)
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        const userInfo: User = {
          id: payload.sub,
          username: payload.username || payload['cognito:username'] || username,
          email: payload.email || '',
        };

        // Use ID token for API authorization (contains user claims)
        setAccessToken(idToken);
        setUser(userInfo);

        // Store both tokens for debugging
        localStorage.setItem('accessToken', idToken);
        localStorage.setItem('idToken', idToken);  
        localStorage.setItem('rawAccessToken', accessToken || '');
        localStorage.setItem('user', JSON.stringify(userInfo));
        
        console.log('Tokens stored:', {
          idToken: idToken?.substring(0, 50) + '...',
          accessToken: accessToken?.substring(0, 50) + '...'
        });
      } else {
        throw new Error('No access token received');
      }
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      setAccessToken(null);
      setUser(null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    return accessToken;
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    signIn,
    signOut,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};